/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';

import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { authorizeHttp } from '../../util/authorize-http';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private oauthService = inject(OAuthService);
  private oauthStorage = inject(OAuthStorage);
  private http = inject(HttpClient);
  private router = inject(Router);

  private clientID = environment.clientID;

  private idToken = signal<string|null>(null);
  private accessToken = signal<string|null>(null);

  private refreshTimeout: number | null = null;

  idTokenReadOnly = computed(() => this.idToken());
  accessTokenReadOnly = computed(() => this.accessToken());
  loggedIn = computed(() => this.idToken() !== null && this.accessToken() !== null);

  email = computed(() => {
    const idToken = this.idTokenReadOnly();
    if (idToken !== null) {
      const decoded = jwtDecode(idToken) as any;
      return decoded['email'];
    }
    return '';
  });

  // roles are extracted from the id token's roles claim for the use in the frontend
  roles = computed(() => {
    let roles = <string[]>[];
    const idToken = this.idTokenReadOnly();
    if (idToken !== null) {
      const decoded = jwtDecode(idToken) as any;
      const rolesString = decoded['roles'];
      if (typeof rolesString === 'string') {
        roles = rolesString.split(' ');
      }
    }
    return [...new Set(roles)];
  });

  // tbsTeams is a custom claim that contains the teams as comma separated list in a string the user is part of
  tbsTeams = computed(() => {
    let tbsTeams = <string[]>[];
    const idToken = this.idTokenReadOnly();
    if (idToken !== null) {
      const decoded = jwtDecode(idToken) as any;
      const tbsTeamsString = decoded['tbs_teams'];
      if (typeof tbsTeamsString === 'string') {
        tbsTeams = tbsTeamsString.split(',').map(team => team.trim());
      }
    }
    return [...new Set(tbsTeams)];
  });

  // tbsCompanyRoles is a custom claim that contains the Application roles as comma separated list in a string the user is assigned to
  tbsCompanyRoles = computed(() => {
    let tbsCompanyRoles = <string[]>[];
    const idToken = this.idTokenReadOnly();
    if (idToken !== null) {
      const decoded = jwtDecode(idToken) as any;
      const tbsCompanyRolesString = decoded['tbs_company_roles'];
      if (typeof tbsCompanyRolesString === 'string') {
        tbsCompanyRoles = tbsCompanyRolesString.split(',').map(role => role.trim());
      }
    }
    return [...new Set(tbsCompanyRoles)];
  });


  givenName = signal<string|null>(null);
  familyName = signal<string|null>(null);
  picture = signal<string|null>(null);

  primaryIdentity = computed(() => {
    const idToken = this.idTokenReadOnly();
    if (idToken !== null) {
      const decoded = jwtDecode(idToken) as any;
      const {identities} = decoded;
      for (const identity of identities) {
        if (identity.primary) {
          return identity;
        }
      }
    }
    return null;
  });

  userID = signal<string|null>(null);

  constructor() {
    (async () => {
      const {issuer, clientID} = await this.tryFetchClientID();

      this.oauthService.configure({...environment.authCodeFlowConfig, issuer, clientId: clientID});
      this.oauthService.loadDiscoveryDocument(`${issuer}/.well-known/openid-configuration`).then(() => {
        this.oauthService.tryLogin().then(() => {
          this.updateTokens();
          if (!this.isTokenValid(this.idToken())) {
            this.idToken.set(null);
            this.accessToken.set(null);
            this.oauthStorage.removeItem('access_token');
            this.oauthStorage.removeItem('id_token');
            this.refreshTokens();
            this.storeRoute();
          } else {
            this.restoreRoute();
          }
        });
      });
    })();

    this.oauthService.timeoutFactor = .75;

    // automatic silent refresh uses an iframe, which is blocked by aws cognito
    // this.oauthService.setupAutomaticSilentRefresh({}, 'access_token', false);

    // this.refreshTokens directly requests a new access token and id token from the token endpoint using the current refresh token
    effect(() => {
      const idToken = this.idToken();
      const accessToken = this.accessToken();
      if (idToken && accessToken) {
        if (this.refreshTimeout) {
          clearTimeout(this.refreshTimeout);
        }
        const expiry = (jwtDecode(idToken).exp || 0) * 1000;
        const now = new Date();
        const remainingTime = Math.max(60000, (expiry - now.getTime()) * (this.oauthService.timeoutFactor || 1));
        this.refreshTimeout = window.setTimeout(() => {
          this.refreshTokens();
        }, remainingTime);
      }

      if (this.oauthService.userinfoEndpoint && accessToken) {
        const headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', `Bearer ${accessToken}`);
        this.http.get<{ family_name: string, given_name: string, picture?: string }>(this.oauthService.userinfoEndpoint, { headers }).subscribe(data => {
          this.picture.set(data.picture || null);
          this.givenName.set(data.given_name);
          this.familyName.set(data.family_name);
        });
      }
    });
  }

  login() {
    this.storeRoute();
    this.oauthService.initCodeFlow();
  }

  logout() {
    this.storeRoute();
    // eslint-disable-next-line camelcase
    this.oauthService.logOut({ client_id: this.clientID, logout_uri: environment.authCodeFlowConfig.logoutUrl });
  }

  storeRoute() {
    const restoreUrl = this.router.getCurrentNavigation()?.initialUrl.toString() || null;
    if (restoreUrl) {
      localStorage.setItem('restore_url', restoreUrl);
    }
  }

  restoreRoute() {
    const restoreUrl = localStorage.getItem('restore_url');
    if (restoreUrl) {
      this.router.navigateByUrl(restoreUrl);
      localStorage.removeItem('restore_url');
    }
  }

  updateTokens() {
    this.idToken.set(this.oauthService.getIdToken());
    this.accessToken.set(this.oauthService.getAccessToken());
  }

  authorize(http: HttpClient) {
    return authorizeHttp(http, this.accessTokenReadOnly);
  }

  isTokenValid(idToken: string | null) {
    if (!idToken) { return false; }
    const expiry = (jwtDecode(idToken).exp || 0) * 1000;
    const now = new Date();
    const remainingTime = expiry - now.getTime();
    return remainingTime > 60000;
  }

  refreshTokens() {
    const refreshToken = this.oauthService.getRefreshToken();

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', this.oauthService.clientId!)
      .set('refresh_token', refreshToken)
      .toString();

    if (this.oauthService.tokenEndpoint) {
      this.http.post<{id_token: string, access_token: string, expires_in: number, token_type: string}>(this.oauthService.tokenEndpoint, body, { headers }).subscribe((data) => {
        this.oauthStorage.setItem('access_token', data.access_token);
        this.oauthStorage.setItem('id_token', data.id_token);
        this.updateTokens();
      });
    }
  }

  async tryFetchClientID(): Promise<{ issuer: string, clientID: string }> {
    if (environment.clientIDSourceUrl) {
      try {
        const config = await new Promise<{issuerurl: string, clientid: string}>((res, rej) => {
          this.http.get<{issuerurl: string, clientid: string}>(environment.clientIDSourceUrl!).subscribe({
            next(value) {
              res(value);
            },
            error(err) {
              rej(err);
            },
          });
        });

        if (config) {
          const issuer = config.issuerurl;
          const clientID = config.clientid;
          return { issuer, clientID };
        }
      } catch (e) { console.error(e) }
    }
    return { issuer: environment.issuer, clientID: environment.clientID };
  }
}
